import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import { Progress } from "@/components/ui/progress";
import {
  Briefcase,
  Clock3,
  Eye,
  TrendingUp,
  ArrowUpRight,
} from "lucide-react";
import { Link } from "react-router-dom";

function MetricCard({ label, value, hint, icon: Icon }) {
  return (
    <div
      data-testid={`metric-${label.toLowerCase().replace(/\s/g, "-")}`}
      className="rounded-xl border border-white/10 bg-zinc-900 p-6 hover:border-white/20 transition-colors"
      style={{ transition: "border-color 0.2s ease" }}
    >
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{label}</div>
        <Icon className="h-4 w-4 text-zinc-500" />
      </div>
      <div className="mt-4 font-display text-5xl font-bold">{value}</div>
      {hint && <div className="mt-2 text-xs text-zinc-500">{hint}</div>}
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [s, c] = await Promise.all([
          api.get("/dashboard/stats"),
          api.get("/clients"),
        ]);
        setStats(s.data);
        setClients(c.data);
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  return (
    <div className="p-8 lg:p-12 relative">
      <PageHeader
        eyebrow="Studio Command Center"
        title="Agency Overview"
        subtitle="Live pulse on active clients, deliverables in flight, and editor bandwidth. Refresh the room in one glance."
      />

      {/* Metrics */}
      <div data-testid="dashboard-metrics" className="grid grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard label="Active Clients" value={stats?.active_clients ?? "—"} hint="Currently on retainer" icon={Briefcase} />
        <MetricCard label="Deliverables Due" value={stats?.deliverables_due_this_month ?? "—"} hint="This month, unapproved" icon={Clock3} />
        <MetricCard label="Pending Reviews" value={stats?.pending_reviews ?? "—"} hint="Awaiting your call" icon={Eye} />
        <MetricCard label="Approved Total" value={stats?.approved_tasks ?? "—"} hint="All-time delivered" icon={TrendingUp} />
      </div>

      {/* Client Health + Editor Workload */}
      <div className="mt-8 grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 rounded-xl border border-white/10 bg-zinc-900 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">02 / Client Health Matrix</div>
              <h2 className="font-display text-2xl font-bold mt-1">Deliverables Progress</h2>
            </div>
            <Link
              to="/clients"
              data-testid="link-view-clients"
              className="text-xs text-zinc-400 hover:text-white flex items-center gap-1"
            >
              View clients <ArrowUpRight className="h-3 w-3" />
            </Link>
          </div>
          <div className="space-y-5">
            {clients.length === 0 && (
              <div className="text-sm text-zinc-500 py-8 text-center">
                No clients yet — seed the demo data or add a client to begin.
              </div>
            )}
            {clients.map((c) => (
              <div key={c.id} data-testid={`client-health-${c.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <div className="font-medium">{c.name}</div>
                    <span className="text-xs font-mono text-zinc-500">
                      {c.completed_count}/{c.package_deliverables_count} reels
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-zinc-500">{c.in_flight_count} in flight</span>
                    <span className="font-mono text-sm font-semibold">{c.progress_pct}%</span>
                  </div>
                </div>
                <Progress value={c.progress_pct} className="h-1.5 bg-white/5" />
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-xl border border-white/10 bg-zinc-900 p-6">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">03 / Editor Bandwidth</div>
          <h2 className="font-display text-2xl font-bold mt-1 mb-6">Workload</h2>
          <div className="space-y-4">
            {(stats?.editor_workload || []).length === 0 && (
              <div className="text-sm text-zinc-500">No editors yet.</div>
            )}
            {(stats?.editor_workload || []).map((e) => (
              <div
                key={e.user_id}
                data-testid={`editor-workload-${e.user_id}`}
                className="flex items-center gap-3"
              >
                {e.picture ? (
                  <img src={e.picture} alt="" className="h-9 w-9 rounded-full object-cover border border-white/10" />
                ) : (
                  <div className="h-9 w-9 rounded-full bg-zinc-800" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{e.name}</div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                    {e.active} active · {e.completed} done
                  </div>
                </div>
                <div
                  className={`font-mono text-xs px-2 py-0.5 rounded-full ${
                    e.active > 4
                      ? "bg-rose-500/10 text-rose-400"
                      : e.active > 2
                      ? "bg-amber-500/10 text-amber-400"
                      : "bg-emerald-500/10 text-emerald-400"
                  }`}
                >
                  {e.active > 4 ? "Heavy" : e.active > 2 ? "Steady" : "Light"}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
